import { useCallback } from "react";
import qz from "qz-tray";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useQzPrint } from "src/hooks/useQzPrint";
import useUserStore from "src/store/useUserStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { ShowSuccessFetch, showError, showSuccess, showWarning } from "src/utils/toast/toastUtil";
import {
    getPdfBlob_PRD3060_R01,
    getPRD3060_R01AllCount,
    getPRD3060_R01DetailList,
    getPRD3060_R01List,
} from "./Api_PRD3060_R01";
import { PRD3060_R01Dto } from "./Dto_PRD3060_R01";
import { toPRD3060_R01RowKey } from "./Table_PRD3060_R01";
import { usePRD3060_R01Store } from "./Store_PRD3060_R01";

interface Props {
    setMessage: (message: string) => void;
}

export const Content_PRD3060_R01 = ({ setMessage }: Props) => {
    const {
        PRD3060_R01Filter,
        setPRD3060_R01PastFilter,
        setPRD3060_R01List,
        setPRD3060_R01DetailList,
        setSelectedPRD3060_R01List,
        setAllCount,
        setScrollTop,
        setDetailScrollTop,
    } = usePRD3060_R01Store();
    const { userInfo } = useUserStore();
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    const { printPdfFromBlob } = useQzPrint();

    useFetchActionbarAuth("PRD3060_R01");

    const fetchData = useCallback(async () => {
        setScrollTop(null);
        setDetailScrollTop(null);
        setLoadingSpinner(true);
        try {
            const response = await getPRD3060_R01List("search", PRD3060_R01Filter);
            const data = response.data.data;

            const countResponse = await getPRD3060_R01AllCount(PRD3060_R01Filter);
            setAllCount(countResponse.data.data);

            if (data && data.length > 0) {
                setPRD3060_R01List(data);
                setPRD3060_R01PastFilter(PRD3060_R01Filter);
                ShowSuccessFetch(
                    setMessage,
                    `생산실적 목록을 조회했습니다. (${data.length}/${countResponse.data.data})`
                );
            } else {
                setPRD3060_R01List([]);
                showWarning("생산실적 목록을 조회할 수 없습니다.");
            }
        } catch (error) {
            console.error("생산실적 목록 조회 중 오류가 발생했습니다.", error);
            showWarning("생산실적 목록을 조회할 수 없습니다.");
            setPRD3060_R01List([]);
        } finally {
            setLoadingSpinner(false);
        }
    }, [PRD3060_R01Filter, setAllCount, setLoadingSpinner, setMessage, setPRD3060_R01List, setPRD3060_R01PastFilter, setScrollTop, setDetailScrollTop]);

    const fetchDetailList = useCallback(async (row: PRD3060_R01Dto) => {
        setDetailScrollTop(null);
        if (!row.productResultNo) {
            setPRD3060_R01DetailList([]);
            showWarning("불량내역이 없습니다.");
            return;
        }
        try {
            const response = await getPRD3060_R01DetailList(row.productResultNo);
            const detailList = response.data.data ?? [];
            setPRD3060_R01DetailList(detailList);
            setSelectedPRD3060_R01List([toPRD3060_R01RowKey(row)]);

            if (detailList.length === 0) {
                showWarning("불량내역이 없습니다.");
            }
        } catch (error) {
            showWarning("생산실적 상세를 조회할 수 없습니다.");
            setPRD3060_R01DetailList([]);
        }
    }, [setPRD3060_R01DetailList, setSelectedPRD3060_R01List, setDetailScrollTop]);

    const handlePrint = useCallback(async () => {
        if (!PRD3060_R01Filter.outputDt) {
            showWarning("출력일을 확인해주세요.");
            return;
        }

        setLoadingSpinner(true);
        try {
            const blob = await getPdfBlob_PRD3060_R01({
                ...PRD3060_R01Filter,
                compNm: userInfo?.compNm ?? "",
            });

            if (qz.websocket.isActive()) {
                const ok = await printPdfFromBlob(blob, "report1");
                if (ok) {
                    showSuccess("PDF 출력이 완료되었습니다.");
                } else {
                    const fileURL = URL.createObjectURL(blob);
                    window.open(fileURL, "_blank");
                    showWarning("PDF를 새 탭에서 열었습니다.");
                }
            } else {
                const fileURL = URL.createObjectURL(blob);
                window.open(fileURL, "_blank");
                showWarning(" PDF를 새 탭에서 열었습니다.");
            }
        } catch (error) {
            console.error("Print error:", error);
            showError("PDF 출력 중 오류가 발생했습니다.");
        } finally {
            setLoadingSpinner(false);
        }
    }, [PRD3060_R01Filter, printPdfFromBlob, setLoadingSpinner, userInfo?.compNm]);

    return { fetchData, fetchDetailList, handlePrint };
};
